import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSummary, getWarnings, getCommendations, getLeaveBalances, getMyLeaves, getEvaluation, getCurrentCycle, acknowledgeEvaluation } from '../lib/db.js';
import { useAuth } from '../lib/auth.jsx';
import { StatusBar } from '../components/StatusBar.jsx';
import { BottomNav } from '../components/BottomNav.jsx';
import { tenureLabel, thaiDate } from '../lib/format.js';

// Staff self-service: shows only the signed-in user's own record.
// RLS (p_*_self_read) guarantees they cannot read anyone else.
export function Me() {
  const { employeeId, signOut } = useAuth();
  const [sum, setSum] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [commends, setCommends] = useState([]);
  const [balances, setBalances] = useState([]);
  const [myLeaves, setMyLeaves] = useState([]);
  const [myEval, setMyEval] = useState(null);
  const [cycleId, setCycleId] = useState(null);
  const [err, setErr] = useState('');
  const [noEmp, setNoEmp] = useState(false);

  useEffect(() => {
    if (!employeeId) { setNoEmp(true); return; }
    let active = true;
    (async () => {
      try {
        const cyc = await getCurrentCycle();
        const [s, w, c, b, lv, ev] = await Promise.all([
          getSummary(employeeId), getWarnings(employeeId), getCommendations(employeeId),
          getLeaveBalances(employeeId), getMyLeaves(employeeId), getEvaluation(employeeId, cyc?.id),
        ]);
        if (!active) return;
        setSum(s);
        setWarnings(w);
        setCommends(c);
        setBalances(b);
        setMyLeaves(lv);
        setCycleId(cyc?.id);
        setMyEval(ev);
      } catch (e) {
        if (active) setErr(e.message);
      }
    })();
    return () => { active = false; };
  }, [employeeId]);

  async function ackEval() {
    try {
      const updated = await acknowledgeEvaluation(employeeId, cycleId);
      setMyEval((p) => ({ ...p, ...updated }));
    } catch (e) { setErr(e.message); }
  }

  const pendingWarnings = warnings.filter((w) => w.ack_status === 'pending');

  return (
    <div className="phone">
      <StatusBar />
      <section className="screen">
        <div className="title-row">
          <div>
            <div className="eyebrow">ของฉัน</div>
            <h1 className="page">{sum ? (sum.nickname || sum.first_name) : 'โปรไฟล์'}</h1>
          </div>
          <span className="glyph">☺</span>
        </div>

        {noEmp && (
          <p className="muted" style={{ marginTop: 20 }}>
            บัญชีนี้ยังไม่ได้ผูกกับข้อมูลพนักงาน — ติดต่อแอดมิน
          </p>
        )}
        {err && <p className="err">{err}</p>}
        {!sum && !noEmp && !err && <div className="center"><div className="spinner" /></div>}

        {sum && (
          <>
            {pendingWarnings.length > 0 && (
              <Link to={`/warnings/${pendingWarnings[0].id}`} className="warn-alert">
                <span className="wa-icon">⚠︎</span>
                <div>
                  <div className="wa-title">คุณได้รับหนังสือตักเตือน {pendingWarnings.length} ฉบับ</div>
                  <div className="wa-sub">กรุณากดเพื่อดูรายละเอียดและรับทราบ ›</div>
                </div>
              </Link>
            )}
            <div className="hero">
              <div className="num small">{Math.max(0, Math.floor((sum.tenure_days ?? 0) / 365))}</div>
              <div className="sub">อายุงาน {tenureLabel(sum.tenure_days)} · {[sum.position, sum.branch].filter(Boolean).join(' · ')}</div>
            </div>
            <hr className="hr" />
            <div className="grid">
              <Stat k="มาสาย (ปีนี้)" v={sum.late_count_ytd ?? 0} unit="ครั้ง" />
              <Stat k="ขาด (ปีนี้)" v={sum.absent_count_ytd ?? 0} unit="ครั้ง" />
              <Stat k="ชมเชย" v={sum.commendation_count ?? 0} unit="ครั้ง" />
              <Stat k="แต้มรางวัล" v={sum.reward_points ?? 0} unit="แต้ม" />
            </div>

            {myEval && (myEval.status === 'submitted' || myEval.status === 'acknowledged') && (
              <>
                <div className="sec-label">ผลประเมินของฉัน</div>
                <div className="card">
                  <div className="row" style={{ paddingTop: 0, border: 0 }}>
                    <div className="ic" style={{ fontWeight: 800 }}>{myEval.overall_score}</div>
                    <div className="main">
                      <div className="l1">คะแนนรวม {myEval.overall_score}/100</div>
                      <div className="l2">รอบครึ่งปีแรก 2569</div>
                    </div>
                    <span className={`tag ${myEval.status === 'acknowledged' ? 'ok' : 'pend'}`}>
                      {myEval.status === 'acknowledged' ? 'รับทราบแล้ว' : 'รอรับทราบ'}
                    </span>
                  </div>
                  {myEval.strengths && <div className="note" style={{ marginTop: 6 }}><b style={{ color: 'var(--ok)' }}>จุดเด่น:</b> {myEval.strengths}</div>}
                  {myEval.improvements && <div className="note" style={{ marginTop: 6 }}><b style={{ color: 'var(--pend)' }}>ควรพัฒนา:</b> {myEval.improvements}</div>}
                  {myEval.status === 'submitted' && (
                    <button className="cta" style={{ marginTop: 14 }} onClick={ackEval}>รับทราบผลประเมิน</button>
                  )}
                </div>
              </>
            )}

            <Link className="row" to={`/attendance/${employeeId}`}>
              <div className="ic">◷</div>
              <div className="main"><div className="l1">สถิติเข้างานรายเดือน</div>
                <div className="l2">ดูสาย/ขาด/ลา แยกความผิดกับสิทธิ</div></div>
              <span className="tag pend">ดู ›</span>
            </Link>

            <div className="sec-label">การลา</div>
            {balances.length > 0 && (
              <div className="grid" style={{ marginBottom: 4 }}>
                {balances.map((b) => (
                  <Stat key={b.code} k={b.name_th} v={b.remaining} unit={`/${b.entitled} วัน`} />
                ))}
              </div>
            )}
            <Link to="/leave/request"><button className="cta" style={{ marginTop: 14 }}>＋ ขอลา</button></Link>
            {myLeaves.map((l) => (
              <div className="row" key={l.id}>
                <div className="ic">🗓</div>
                <div className="main">
                  <div className="l1">{l.name_th || l.leave_types?.name_th || 'ลา'}</div>
                  <div className="l2">{thaiDate(l.start_date)}{l.end_date !== l.start_date ? `–${thaiDate(l.end_date)}` : ''} · {l.days} วัน</div>
                </div>
                <span className={`tag ${leaveTag(l.status)}`}>{leaveStatusTh(l.status)}</span>
              </div>
            ))}

            <div className="sec-label">ผลงาน &amp; ชมเชย ของฉัน</div>
            {commends.length === 0 && <p className="muted">ยังไม่มีใบชมเชย — สู้ๆ นะ 💪</p>}
            {commends.map((c) => (
              <div className="row" key={c.id}>
                <div className="ic" style={{ color: 'var(--ok)' }}>★</div>
                <div className="main">
                  <div className="l1">{c.title}</div>
                  <div className="l2">{meCat(c.category)} · {thaiDate(c.occurred_on)}</div>
                </div>
                {c.reward_points > 0
                  ? <span className="tag reward-tag">+{c.reward_points} แต้ม</span>
                  : <span className="tag ok">ชมเชย</span>}
              </div>
            ))}

            <div className="sec-label">ใบเตือนของฉัน</div>
            {warnings.length === 0 && <p className="muted">ไม่มีใบเตือนมีผล</p>}
            {warnings.map((w) => (
              <Link className="row" key={w.id} to={`/warnings/${w.id}`}>
                <div className="ic">⚠︎</div>
                <div className="main">
                  <div className="l1">{w.incident_detail || 'หนังสือตักเตือน'}</div>
                  <div className="l2">ออก {thaiDate(w.incident_date)} · ขั้นที่ {w.ladder_level}</div>
                </div>
                <span className={`tag ${w.ack_status === 'acknowledged' ? 'ok' : 'pend'}`}>
                  {w.ack_status === 'acknowledged' ? 'รับทราบแล้ว' : 'รอรับทราบ ›'}
                </span>
              </Link>
            ))}
          </>
        )}

        <button className="cta ghost" style={{ marginTop: 30 }} onClick={signOut}>ออกจากระบบ</button>
      </section>
      <BottomNav />
    </div>
  );
}

const meCat = (c) => ({ performance: 'ผลงานดีเด่น', service: 'บริการลูกค้า', teamwork: 'ช่วยทีม', other: 'อื่นๆ' }[c] || 'ชมเชย');
const leaveStatusTh = (s) => ({ pending: 'รออนุมัติ', approved: 'อนุมัติแล้ว', rejected: 'ไม่อนุมัติ', cancelled: 'ยกเลิก' }[s] || s);
const leaveTag = (s) => ({ approved: 'ok', pending: 'pend', rejected: 'warn' }[s] || 'pend');

function Stat({ k, v, unit }) {
  return (
    <div className="stat">
      <div className="k">{k}</div>
      <div className="v">{v} <small>{unit}</small></div>
    </div>
  );
}
